import { Link, useParams } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { Loading, LoadError } from '../components/Loading';
import Crumbs from '../components/Crumbs';
import ProgressBar from '../components/ProgressBar';
import type { SectionProgress } from '../../../shared/types';

export default function BookPage() {
  const { bookId = '' } = useParams();
  const api = useApi();

  const book = useAsync(() => catalogApi.book(bookId, api), [bookId, api]);
  // Progress is a separate request on purpose: the catalog half is cached for
  // the session, while this half changes every time a session is recorded.
  const progress = useAsync(() => progressApi.book(bookId, api), [bookId, api]);

  if (book.loading) {
    return (
      <main className="page">
        <Loading what="chapters" />
      </main>
    );
  }
  if (book.error || !book.data) {
    return (
      <main className="page">
        <LoadError message={book.error ?? 'Book not found'} onRetry={book.reload} />
      </main>
    );
  }

  const data = book.data;
  const bySection = new Map<string, SectionProgress>(
    (progress.data?.sections ?? []).map((section) => [section.sectionId, section]),
  );

  /**
   * A chapter's mastery is its words known over its words total — not the mean
   * of its sections' percentages, which would let an eight-card section weigh
   * as much as a thirty-seven-card one.
   */
  const chapterMastery = (sectionIds: string[]): { known: number; total: number } => {
    let known = 0;
    let total = 0;
    for (const id of sectionIds) {
      const section = bySection.get(id);
      if (!section) continue;
      known += section.cardsKnown;
      total += section.cardCount;
    }
    return { known, total };
  };

  const overall =
    progress.data && progress.data.cardCount > 0
      ? (progress.data.cardsKnown / progress.data.cardCount) * 100
      : 0;

  return (
    <main className="page">
      <Crumbs
        items={[
          { label: 'Languages', to: '/' },
          { label: data.languageCode.toUpperCase(), to: `/languages/${data.languageCode}` },
          { label: data.title },
        ]}
      />

      <header className="page-header">
        <h1>{data.title}</h1>
        <p className="muted">
          {data.author} · {data.level}
        </p>
        <p className="page-description">{data.description}</p>
      </header>

      {progress.data && (
        <div className="book-progress">
          <ProgressBar
            value={overall}
            label={`${progress.data.cardsKnown} of ${progress.data.cardCount} words known`}
          />
        </div>
      )}

      <div className="section-head">
        <h2>Chapters</h2>
        {data.availableChapterCount < data.chapterCount && (
          <span className="muted">
            {data.availableChapterCount} of {data.chapterCount} transcribed so far
          </span>
        )}
      </div>

      <ul className="row-list">
        {data.chapters.map((chapter) => {
          const { known, total } = chapterMastery(chapter.sectionIds);
          return (
            <li key={chapter.id}>
              <Link to={`/chapters/${chapter.id}`} className="row">
                <span className="row-number">{chapter.number}</span>
                <span className="row-body">
                  <span className="row-title">{chapter.title}</span>
                  <span className="row-summary">{chapter.summary}</span>
                  <span className="row-meta">
                    {chapter.sectionCount} sections · {chapter.cardCount} cards
                    {total > 0 && known > 0 && ` · ${known} known`}
                  </span>
                  {total > 0 && (
                    <ProgressBar value={(known / total) * 100} compact />
                  )}
                </span>
                <span className="row-chevron" aria-hidden="true">
                  ›
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      {data.availableChapterCount < data.chapterCount && (
        <p className="muted footnote">
          Chapters {data.availableChapterCount + 1}–{data.chapterCount} have not been transcribed
          yet. Each one is authored under <code>content/books/{data.id}/</code> and picked up by
          the content build.
        </p>
      )}
    </main>
  );
}
